import { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import EditModalFooter from './EditModalFooter'
import type { PatchCarRentalItemViewModel } from './models'
import {
  extractEditableEndDateTimeStrings,
  extractEditableStartDateTimeStrings,
  updateCarRentalPayloadFromViewModel,
} from './utils'
import { formOptions } from '../Common/Fields/config'
import DeleteTripItem from '../DeleteTripItem'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import CarFields from 'tripPlanner/components/TripModal/TripItems/Common/CarFields'
import { usePatchTripItem } from 'tripPlanner/hooks/api/tripItem'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import type { CarRentalItem } from 'tripPlanner/types/tripItem'

interface Props {
  item: CarRentalItem
}

const itemToView = (item: Props['item']): PatchCarRentalItemViewModel => {
  return {
    type: item.type,
    price: item.price,
    url: item.url,
    isBooked: item.isBooked,
    notes: item.notes,
    companyName: item.companyName,
    ...extractEditableStartDateTimeStrings(item),
    ...extractEditableEndDateTimeStrings(item),
    startPlace: item.startPlace,
    endPlace: item.endPlace,
    isEndDateSame: false,
  }
}

function EditCar(props: Props) {
  const { item } = props
  const intl = useIntl()

  const openModal = useOpenModal()
  const { closeModal } = useModalCallbacks()

  const { mutate: updateTripItem, isLoading } = usePatchTripItem({
    onSuccess: () => {
      closeModal()
    },
  })

  const deleteItem = useCallback(
    () => openModal(DeleteTripItem, { item }, { height: 'auto' }),
    [openModal, item],
  )

  const updateItem = useCallback(
    (viewModel: PatchCarRentalItemViewModel) => {
      updateTripItem({
        tripId: item.tripId,
        tripItemId: item.id,
        tripItem: updateCarRentalPayloadFromViewModel(viewModel),
      })
    },
    [item.id, item.tripId, updateTripItem],
  )

  const methods = useForm<PatchCarRentalItemViewModel>({
    ...formOptions,
    defaultValues: itemToView(item),
  })

  const { handleSubmit } = methods

  const modalTitle = intl.formatMessage({
    id: 'GyRYK26Ds5u2yuPeG28n',
    defaultMessage: 'Edit Car',
    description: 'Modal title when editing a car rental item in the trip planner',
  })

  return (
    <>
      <ModalHeader title={modalTitle} onCloseButtonClick={closeModal} />
      <ModalBody>
        <FormProvider {...methods}>
          <ModalContent>
            <CarFields tripId={item.tripId} />
          </ModalContent>
        </FormProvider>
      </ModalBody>
      <EditModalFooter
        deleteItem={deleteItem}
        onSubmit={handleSubmit(updateItem)}
        isLoading={isLoading}
      />
      {isLoading && <LoadingIndicator floating />}
    </>
  )
}

export default EditCar
